import NewsquawkHeadlineItem from "../newsquawk-headlines/NewsquawkHeadlineItem";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { CommonWidgetProps } from "../types";
import { Headline } from "../newsquawk-headlines/models/Headline";
import DeleteConfirmationDialog from "../newsquawk-headlines/DeleteConfirmationDialog";
import EditHeadlineFormDialog from "../newsquawk-headlines/EditHeadlineFormDialog";
import PollingModeIndicator from "@/shared/components/PollingModeIndicator";
import EmptyPlaceholder from "@/ui-components/empty-placeholder";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { usePortfolioBuilderOptional } from "@/pages/PortfolioBuilder/components/PortfolioBuilderContext";
import { applyFilters } from "../newsquawk-headlines/hooks/useHeadlinesHighlightFilterMenu";
import { useHeadlinesFilterPanel } from "../newsquawk-headlines/hooks/useHeadlinesFilterPanel";
import { useHeadlinesHighlightPanel } from "../newsquawk-headlines/hooks/useHeadlinesHighlightPanel";
import FilterHighlightPanels from "@/ui-components/FilterPanel/FilterHighlightPanels";
import useStableArray from "@/hooks/useStableArray";
import { useHeadlineQueriesCRUD } from "../newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { CategoryQuery } from "../newsquawk-headlines/models/CategoryQuery";
import {
  buildSearchTitle,
  mapPortfolioIdsToPortfolios,
} from "../firehose-widgets/shared/hooks/searchUtils";
import {
  useHeadlinesCRUD,
  useSearchHeadlines,
  useIsPollingMode,
} from "@/shared/api/newsquawk-headlines";
import { useHeadlinesFilterStore } from "@/shared/api/newsquawk-headlines/HeadlinesFilterStore";
import { buildHighlightCandidates } from "@/shared/utils/highlightMatches";
import { combinedAlertIds } from "@/shared/utils/alertLists";
import { mergeCombinedFilters } from "@/shared/utils/combinedFilters";
import { trackFilterApplied } from "@/utils/gtm";
import { menuCategories } from "../newsquawk-headlines/models/categories";
import {
  VirtuosoWrapper,
  GroupingUtils,
} from "@/ui-components/grouped-virtuoso";
import { DateSectionHeader } from "../firehose-widgets/shared/DateSectionHeader";
import { checkableTagIds } from "../create-headline/models/HeadlineForm";
import { useAccessControl } from "@/hooks/useAccessControl";
import SearchBar from "@/ui-components/search-bar";

const RESEARCH_TAG_ID = checkableTagIds.Research;

/** Resolve tag IDs to category names using the static menuCategories list */
const allCategoryItems = menuCategories.flatMap((group) => group.items);
function resolveTagNames(tagIds: (string | number)[]): string {
  return tagIds
    .map((id) => allCategoryItems.find((item) => item.id === Number(id))?.title)
    .filter(Boolean)
    .join(", ");
}

function ResearchHeadlines(props: CommonWidgetProps) {
  const { hasLiveAccess } = useAccessControl();
  const isPollingMode = useIsPollingMode();

  const {
    searchHeadlines,
    searchResults,
    isLoading: searchLoading,
    loadNext: loadNextSearchPage,
    hasMore: hasMoreSearchResults,
    isLoadingMore: isLoadingMoreSearchResults,
  } = useSearchHeadlines();

  const { deleteMutation } = useHeadlinesCRUD();

  const [searchQuery, setSearchQuery] = useState("");
  const [editingItem, setEditingItem] = useState<Headline | null>(null);
  const [deletingItem, setDeletingItem] = useState<Headline | null>(null);


  const { portfolios } = usePortfoliosCRUD();
  const { queriesQuery: categoryQueries } = useHeadlineQueriesCRUD();
  const portfolioBuilderContext = usePortfolioBuilderOptional();

  const [lastOpenedPanel, setLastOpenedPanel] = useState<
    "filter" | "highlight" | null
  >(null);

  const handleHighlightPanelOpen = useCallback(() => {
    setLastOpenedPanel("highlight");
  }, []);

  const handleFilterPanelOpen = useCallback(() => {
    setLastOpenedPanel("filter");
  }, []);

  // Highlight panel
  const {
    selections: highlightSelections,
    setSelections: setHighlightSelections,
    isPanelOpen: isHighlightPanelOpen,
    closePanel: closeHighlightPanel,
    filterMenus: highlightMenus,
    clearSelections: clearHighlightSelections,
    order: highlightOrder,
    moveInOrder: moveHighlight,
  } = useHeadlinesHighlightPanel({
    setActions: props.headerOptions?.setActions,
    pushAction: props.headerOptions?.pushAction,
    persistKey: props.widgetCompositeId,
    onOpen: handleHighlightPanelOpen,
    closeSearch: props.headerOptions?.closeSearch,
    onSearchOpen: props.headerOptions?.onSearchOpen,
  });

  // Filter panel
  const {
    selections: filterSelections,
    setSelections: setFilterSelections,
    isPanelOpen: isFilterPanelOpen,
    closePanel: closeFilterPanel,
    filterMenus,
    clearSelections: clearFilterSelections,
    filterMode,
    tagWatchlists,
  } = useHeadlinesFilterPanel({
    setActions: props.headerOptions?.setActions,
    pushAction: props.headerOptions?.pushAction,
    persistKey: props.widgetCompositeId,
    onOpen: handleFilterPanelOpen,
    closeSearch: props.headerOptions?.closeSearch,
    onSearchOpen: props.headerOptions?.onSearchOpen,
  });

  // Panel coordination: when one opens, close the other
  useEffect(() => {
    if (lastOpenedPanel === "filter" && isHighlightPanelOpen) {
      closeHighlightPanel();
    } else if (lastOpenedPanel === "highlight" && isFilterPanelOpen) {
      closeFilterPanel();
    }
  }, [
    lastOpenedPanel,
    isFilterPanelOpen,
    isHighlightPanelOpen,
    closeFilterPanel,
    closeHighlightPanel,
  ]);

  // Sync preview state from PortfolioBuilder to filter selections
  useEffect(() => {
    if (!portfolioBuilderContext) return;

    const { previewState } = portfolioBuilderContext;

    if (previewState.type === "watchlist" && previewState.id !== null) {
      setFilterSelections((prev) => ({
        ...prev,
        "ticker-watchlists-filter": [String(previewState.id)],
        "keyword-watchlists-filter": [],
        "category-filter": [],
        "basic-filter": [],
        "tag-watchlists-filter": [],
      }));
    } else if (previewState.type === "filter" && previewState.id !== null) {
      setFilterSelections((prev) => ({
        ...prev,
        "category-filter": [String(previewState.id)],
        "ticker-watchlists-filter": [],
        "keyword-watchlists-filter": [],
        "basic-filter": [],
        "tag-watchlists-filter": [],
      }));
    } else if (previewState.type === "tagWatchlist" && previewState.tagIds) {
      const tagIds = previewState.tagIds;
      setFilterSelections((prev) => ({
        ...prev,
        "basic-filter": tagIds,
        "ticker-watchlists-filter": [],
        "keyword-watchlists-filter": [],
        "category-filter": [],
        "tag-watchlists-filter": [],
      }));
    } else {
      setFilterSelections((prev) => ({
        ...prev,
        "ticker-watchlists-filter": [],
        "keyword-watchlists-filter": [],
        "category-filter": [],
        "basic-filter": [],
        "tag-watchlists-filter": [],
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioBuilderContext?.previewState, setFilterSelections]);

  // Sync portfolio filter/highlight selections to global store for sound alerts
  // Identifies this widget instance in the shared alert store, so sibling
  // Headlines widgets don't overwrite (or on unmount erase) each other's
  // selections. Popouts/standalone renders have no composite id.
  const fallbackInstanceId = useId();
  const instanceKey = props.widgetCompositeId ?? fallbackInstanceId;

  const setPortfoliosFilter = useHeadlinesFilterStore(
    (state) => state.setPortfoliosFilter
  );
  const setPortfoliosHighlight = useHeadlinesFilterStore(
    (state) => state.setPortfoliosHighlight
  );

  useEffect(() => {
    const highlightPortfolioIds = [
      ...[
        ...(highlightSelections["ticker-watchlists-highlight"] ?? []),
        ...(highlightSelections["keyword-watchlists-highlight"] ?? []),
      ].map((id) => Number(id)),
      // Combined watchlists alert on their own settings too (negative ids).
      ...combinedAlertIds(highlightSelections["category-highlight"]),
    ];
    const filterPortfolioIds = [
      ...[
        ...(filterSelections["ticker-watchlists-filter"] ?? []),
        ...(filterSelections["keyword-watchlists-filter"] ?? []),
      ].map((id) => Number(id)),
      ...combinedAlertIds(filterSelections["category-filter"]),
    ];

    setPortfoliosHighlight(instanceKey, highlightPortfolioIds);
    setPortfoliosFilter(instanceKey, filterPortfolioIds);

    return () => {
      setPortfoliosHighlight(instanceKey, []);
      setPortfoliosFilter(instanceKey, []);
    };
  }, [
    filterSelections,
    highlightSelections,
    instanceKey,
    setPortfoliosFilter,
    setPortfoliosHighlight,
  ]);

  const basicFilterSelected = useStableArray(
    filterSelections["basic-filter"] || []
  );

  const tagWatchlistsFilterSelected = useStableArray(
    filterSelections["tag-watchlists-filter"] || []
  );

  const tagWatchlistTagIds = useMemo(() => {
    if (tagWatchlistsFilterSelected.length === 0) return [];
    return tagWatchlistsFilterSelected.flatMap((watchlistId) => {
      const watchlist = tagWatchlists.find((w) => String(w.id) === watchlistId);
      return watchlist?.tags || [];
    });
  }, [tagWatchlistsFilterSelected, tagWatchlists]);

  const queriesToSearch = useStableArray(
    filterSelections["category-filter"]
      ?.map((id) => {
        if (categoryQueries.data) {
          return categoryQueries.data.find((query) => String(query.id) === id);
        }
        return undefined;
      })
      .filter(Boolean) || []
  ) as CategoryQuery[];

  const advancedFilterTagIds = useMemo(() => {
    if (queriesToSearch.length === 0) return [];
    return queriesToSearch.flatMap((query) =>
      (query.tagsWatchlists || []).flatMap((tw) => tw.tags)
    );
  }, [queriesToSearch]);

  const portfoliosFilterSelected = useStableArray([
    ...(filterSelections["ticker-watchlists-filter"] || []),
    ...(filterSelections["keyword-watchlists-filter"] || []),
  ]);

  const hasUserTagFilters =
    basicFilterSelected.length > 0 ||
    tagWatchlistTagIds.length > 0 ||
    advancedFilterTagIds.length > 0;

  const hasUserFilters =
    queriesToSearch.length > 0 ||
    hasUserTagFilters ||
    portfoliosFilterSelected.length > 0;

  // When user has tag filters, post-filter to keep only Research-tagged headlines
  const researchFilteredResults = useMemo(
    () =>
      hasUserTagFilters
        ? searchResults.filter((h) => h.tag_id_list.includes(RESEARCH_TAG_ID))
        : searchResults,
    [searchResults, hasUserTagFilters]
  );

  // The widget's highlighted lists — watchlists and combined watchlists — in
  // its order, as the Headlines widget does.
  const highlightCandidates = useMemo(
    () =>
      buildHighlightCandidates(highlightOrder, portfolios, categoryQueries.data),
    [highlightOrder, portfolios, categoryQueries.data]
  );

  // Apply highlight filters to search results
  const filteredData = applyFilters(researchFilteredResults, highlightCandidates);


  // Push polling mode indicator into widget header
  useEffect(() => {
    if (isPollingMode && hasLiveAccess) {
      props.headerOptions?.pushAction(
        <PollingModeIndicator key="00-polling-mode" />
      );
    }
  }, [isPollingMode, hasLiveAccess, props.headerOptions]);

  // Search effect: sends Research tag as base constraint OR user-selected tags only
  useEffect(() => {
    const portfolioFilterIds = portfoliosFilterSelected.map(String);
    const portfoliosToSearch = mapPortfolioIdsToPortfolios(
      portfolios,
      portfolioFilterIds
    );

    // When user has tag filters, send only those (not Research) so the API narrows.
    // Post-filtering for Research tag happens in researchFilteredResults above.
    // When no user tags, use Research tag as the base constraint.
    const allTagIds = hasUserTagFilters
      ? [
          ...new Set([
            ...basicFilterSelected.map(Number),
            ...tagWatchlistTagIds.map(Number),
            ...advancedFilterTagIds.map(Number),
          ]),
        ]
      : [RESEARCH_TAG_ID];

    // Detect quoted search for exact match
    const isQuotedSearch =
      searchQuery.startsWith('"') && searchQuery.endsWith('"') && searchQuery.length > 2;
    const cleanQuery = isQuotedSearch ? searchQuery.slice(1, -1) : searchQuery;

    searchHeadlines({
      query: cleanQuery || undefined,
      subscribed: true,
      exactMatch: isQuotedSearch,
      rows: 50,
      portfolios: portfoliosToSearch.length > 0 ? portfoliosToSearch : undefined,
      advancedFilter: mergeCombinedFilters(queriesToSearch),
      tags: allTagIds,
    });

    // Build title based on active user filters
    if (hasUserFilters) {
      const title = buildSearchTitle({
        selectedAdvancedFilters: queriesToSearch,
        selectedFilterPortfolio: portfoliosToSearch,
        defaultTitle: "Newsquawk Research Reports",
      });

      if (title) {
        props.headerOptions?.setTitle(title);
      } else if (
        basicFilterSelected.length > 0 ||
        tagWatchlistTagIds.length > 0
      ) {
        props.headerOptions?.setTitle("Filtered");
      } else {
        props.headerOptions?.setTitle("Newsquawk Research Reports");
      }
    } else {
      props.headerOptions?.setTitle("Newsquawk Research Reports");
    }
  }, [
    queriesToSearch,
    basicFilterSelected,
    portfoliosFilterSelected,
    tagWatchlistTagIds,
    advancedFilterTagIds,
    props.headerOptions,
    searchHeadlines,
    portfolios,
    filterMode,
    hasUserFilters,
    hasUserTagFilters,
    searchQuery,
  ]);

  // Track filter usage (debounced)
  const filterTrackingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (filterTrackingTimer.current) clearTimeout(filterTrackingTimer.current);
    filterTrackingTimer.current = setTimeout(() => {
      if (basicFilterSelected.length > 0) {
        trackFilterApplied({
          filter_context: "research-headlines",
          filter_type: "category",
          filter_value: resolveTagNames(basicFilterSelected) || basicFilterSelected.join(", "),
          filter_count: basicFilterSelected.length,
        });
      }
      if (portfoliosFilterSelected.length > 0) {
        const portfolioNames = portfoliosFilterSelected
          .map((id) => portfolios?.find((p) => String(p.id) === String(id))?.name)
          .filter(Boolean)
          .join(", ");
        trackFilterApplied({
          filter_context: "research-headlines",
          filter_type: "portfolio",
          filter_value: portfolioNames || portfoliosFilterSelected.join(", "),
          filter_count: portfoliosFilterSelected.length,
        });
      }
      if (tagWatchlistTagIds.length > 0) {
        trackFilterApplied({
          filter_context: "research-headlines",
          filter_type: "tag_watchlist",
          filter_value: resolveTagNames(tagWatchlistTagIds) || "tag_watchlist",
          filter_count: tagWatchlistTagIds.length,
        });
      }
      if (queriesToSearch.length > 0) {
        trackFilterApplied({
          filter_context: "research-headlines",
          filter_type: "advanced_filter",
          filter_value: queriesToSearch.map((q) => q.queryName).join(", "),
          filter_count: queriesToSearch.length,
        });
      }
    }, 500);
    return () => {
      if (filterTrackingTimer.current) clearTimeout(filterTrackingTimer.current);
    };
  }, [basicFilterSelected, portfoliosFilterSelected, tagWatchlistTagIds, queriesToSearch, portfolios]);

  return (
    <>
      <div className="h-full flex flex-col relative">
        <div className="p-2">
          <SearchBar
            loading={searchLoading}
            onSearch={(q) => setSearchQuery(q)}
            onClear={() => setSearchQuery("")}
            className="relative z-30"
          />
        </div>

        {/* Filter Panel - inline above content */}
        <FilterHighlightPanels
        filterMenus={filterMenus}
        filterSelections={filterSelections}
        setFilterSelections={setFilterSelections}
        isFilterOpen={isFilterPanelOpen}
        onClearFilter={clearFilterSelections}
        onCloseFilter={closeFilterPanel}
        highlightMenus={highlightMenus}
        highlightSelections={highlightSelections}
        setHighlightSelections={setHighlightSelections}
        isHighlightOpen={isHighlightPanelOpen}
        onClearHighlight={clearHighlightSelections}
        onCloseHighlight={closeHighlightPanel}
        highlightOrder={highlightOrder}
        onMoveHighlight={moveHighlight}
      />

        {/* Headlines list */}
        <VirtuosoWrapper
          data={filteredData}
          grouping={{
            groupBy: (items) =>
              GroupingUtils.groupByDate(items, (item) => {
                const h = item[0];
                return typeof h.published_at === "object"
                  ? new Date(h.published_at.unix * 1000).toISOString()
                  : (h.published_at as string);
              }, {
                includeYesterday: true,
                dateFormat: { weekday: "short", month: "long", day: "numeric", year: "numeric" },
              }),
            hideFirstGroupHeader: true,
          }}
          scrollManager={{
            enabled: true,
            compareFirstItem: (a, b) => a?.[0]?.id === b?.[0]?.id,
          }}
          pagination={{
            hasMore: hasMoreSearchResults,
            isLoadingMore: isLoadingMoreSearchResults,
            loadNext: loadNextSearchPage,
          }}
          renderItem={([headline, highlightInfo]) => (
            <NewsquawkHeadlineItem
              headline={headline}
              key={headline.id}
              highlightInfo={highlightInfo}
              onItemDelete={setDeletingItem}
              onItemEdit={setEditingItem}
            />
          )}
          renderGroupHeader={(title) => <DateSectionHeader title={title} />}
          renderEmpty={() => (
            <EmptyPlaceholder
              isLoading={searchLoading}
              delay={200}
            />
          )}
          className="flex-1 scrollbar-thin bg-white dark:bg-gray-900 relative"
        />
      </div>

      {/* Dialogs */}
      <section>
        <DeleteConfirmationDialog
          isOpen={deletingItem !== null}
          onClose={() => setDeletingItem(null)}
          onConfirmDelete={
            deletingItem
              ? () => deleteMutation.mutateAsync(deletingItem.id)
              : undefined
          }
        />

        <EditHeadlineFormDialog
          isOpen={editingItem !== null}
          headline={editingItem}
          onClose={() => setEditingItem(null)}
        />
      </section>
    </>
  );
}

export default ResearchHeadlines;
